import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from '../../i18n';
import CardNB from '../../components/ui-nb/CardNB';
import IconNB from '../../components/ui-nb/IconNB';
import { useOrdersList } from '../../hooks/useOrdersList';
import OrderCard from '../../components/technician/OrderCard';

const STATUS_TABS = [
  { key: null, label: 'All' },
  { key: 'Received', label: 'Received' },
  { key: 'Diagnosing', label: 'Diagnosing' },
  { key: 'Waiting for part', label: 'Waiting' },
  { key: 'In repair', label: 'In repair' },
  { key: 'Testing', label: 'Testing' },
  { key: 'Ready for delivery', label: 'Ready' },
];

export default function TechnicianHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Auth protection
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  const { orders, isLoading, error, filterByStatus, filterBySearch } = useOrdersList(userId || null);

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto pb-24 bg-nb-cream p-8">
        <p className="text-nb-text-muted text-center">Redirecting to login...</p>
      </div>
    );
  }

  const displayedOrders = (() => {
    let result = activeTab ? filterByStatus(activeTab) : orders;
    if (searchTerm.trim()) {
      result = filterBySearch(searchTerm);
      if (activeTab) result = result.filter(o => o.status === activeTab);
    }
    // Sort: urgent first
    return [...result].sort((a, b) => {
      if (a.priority === 'urgent' && b.priority !== 'urgent') return -1;
      if (a.priority !== 'urgent' && b.priority === 'urgent') return 1;
      return 0;
    });
  })();

  return (
    <div className="max-w-4xl mx-auto pb-24 bg-nb-cream">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-black font-nb mb-1">
            {t('technician.title')}
          </h1>
          {isLoading ? (
            <p className="text-sm mt-1 text-nb-text-muted">{t('common.loading')}</p>
          ) : (
            <p className="text-sm mt-1 text-nb-text-muted">
              {orders.length} {t('technician.assignedEquipment')}
            </p>
          )}
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key ?? 'all'}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-[2px] whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-nb-black text-white border-2 border-black'
                : 'bg-white text-black border-2 border-black hover:bg-nb-yellow'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by customer or device..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2.5 bg-white border-2 border-black text-black focus:outline-none focus:border-nb-yellow rounded-[2px]"
        />
      </div>

      {/* Error state */}
      {error && (
        <CardNB className="mb-4 p-4 border-2 border-nb-red bg-white">
          <p className="text-sm text-nb-red">{error}</p>
        </CardNB>
      )}

      {/* Empty state */}
      {displayedOrders.length === 0 && !isLoading && (
        <CardNB className="p-12 text-center">
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg font-semibold text-black mb-2">{t('technician.noEquipment')}</h3>
          <p className="text-nb-text-muted">{t('technician.noEquipmentDesc')}</p>
        </CardNB>
      )}

      {/* Order list */}
      {!isLoading && displayedOrders.length > 0 && (
        <div className="space-y-4">
          {displayedOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              href={`/en/technician/equipment/${order.id}`}
            />
          ))}
        </div>
      )}

      <Link
        to="/en/technician/receive"
        className="fixed bottom-8 right-8 w-14 h-14 flex items-center justify-center bg-nb-black border-3 border-black hover:bg-nb-yellow transition-colors"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <IconNB name="plus" size={28} className="text-white" />
      </Link>
    </div>
  );
}